import type { FavouriteColumnPresetModel, SavedColumnView } from './columnViewTypes';
import { ALL_COLUMNS, FAVOURITE_COLUMN_PRESET_KEY, PRESET_INDEX } from './columnVisibilitySelectorConstants';

export type PreferenceLike = {
    view?: string;
    accountId?: string;
    application?: string;
    key?: string;
    value?: unknown;
};

export function stripFavouriteFields(
    model: FavouriteColumnPresetModel | Record<string, unknown> | null | undefined
): Record<string, boolean> {
    const copy: Record<string, unknown> = { ...(model ?? {}) };
    delete copy.currentFavourite;
    delete copy.currentIndex;
    delete copy.presetKey;
    delete copy.orderedFields;
    delete copy.savedViewId;
    return copy as Record<string, boolean>;
}

function parsePreferenceValue(value: unknown) {
    if (typeof value === 'string') {
        try {
            return JSON.parse(value);
        } catch {
            return null;
        }
    }
    if (value && typeof value === 'object') return value as Record<string, unknown>;
    return null;
}

/**
 * Finds the current user's favourite column preset for a grid.
 * Prefers the dedicated key; falls back to legacy per-preset currentFavourite flags.
 * Pass `applicationName` to scope to the current app — the same grid id can hold a favourite per application.
 */
export function findOwnFavouritePreference(
    preferences: PreferenceLike[] | undefined,
    viewId: string,
    accountId?: string,
    applicationName?: string
) {
    function isOwnGridPreference(pref: PreferenceLike) {
        return (
            pref.view === viewId &&
            pref.accountId === accountId &&
            (applicationName === undefined || pref.application === applicationName)
        );
    }

    const dedicated = preferences?.find(
        (pref) => isOwnGridPreference(pref) && pref.key === FAVOURITE_COLUMN_PRESET_KEY
    );
    if (dedicated) return dedicated;

    return preferences?.find((pref) => {
        if (!isOwnGridPreference(pref) || pref.key === FAVOURITE_COLUMN_PRESET_KEY) {
            return false;
        }
        const parsed = parsePreferenceValue(pref.value);
        return parsed?.currentFavourite === true;
    });
}

export function getFavouritePreferenceModel(
    favourite: PreferenceLike | undefined
): FavouriteColumnPresetModel | null {
    const parsed = favourite ? parsePreferenceValue(favourite.value) : null;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return null;
    }
    return parsed as FavouriteColumnPresetModel;
}

export function getFavouritePresetLabel(
    favourite: PreferenceLike | undefined,
    parsed: FavouriteColumnPresetModel | Record<string, unknown> | null
) {
    if (typeof parsed?.presetKey === 'string') return parsed.presetKey;
    return favourite?.key;
}

/** Favourite payload for the built-in All columns preset. */
export function buildAllColumnsFavouriteModel(): FavouriteColumnPresetModel {
    return {
        presetKey: ALL_COLUMNS,
        currentFavourite: true,
        currentIndex: PRESET_INDEX.all
    };
}

/** True when the favourite references a saved view id that no longer exists. */
export function isStaleSavedColumnViewFavourite(
    parsed: FavouriteColumnPresetModel | Record<string, unknown> | null | undefined,
    savedColumnViews: SavedColumnView[]
): boolean {
    if (!parsed) {
        return false;
    }
    const savedViewId = typeof parsed.savedViewId === 'string' ? parsed.savedViewId : undefined;
    const isSavedPreset = parsed.currentIndex === PRESET_INDEX.saved || Boolean(savedViewId);
    if (!isSavedPreset || !savedViewId) {
        return false;
    }
    return !savedColumnViews.some((view) => view.id === savedViewId);
}
